import { FormEvent, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { resetPasswordService } from "../api/services";
import { PasswordInput } from "../components/PasswordInput";
import { useToast } from "../components/Toast";
import { useRequestError } from "../hooks/useRequestError";

export const ResetPasswordPage = () => {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { showToast } = useToast();
  const { handleRequestError } = useRequestError();

  const routeToken = (params.get("token") || "").trim();
  const [manualToken, setManualToken] = useState("");
  const [password, setPassword] = useState("");
  const [passwordConfirm, setPasswordConfirm] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showPasswordConfirm, setShowPasswordConfirm] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const token = useMemo(
    () => routeToken || manualToken.trim(),
    [routeToken, manualToken]
  );

  const onSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setError(null);
    if (!token) {
      setError("Reset link is missing or invalid. Request a new one.");
      return;
    }
    if (password.length < 6) {
      setError("Password must be at least 6 characters");
      return;
    }
    if (password !== passwordConfirm) {
      setError("Passwords do not match");
      return;
    }
    setSubmitting(true);
    try {
      await resetPasswordService(token, password);
      showToast({
        type: "success",
        title: "Password updated",
        message: "You can now log in with your new password.",
      });
      navigate("/login", { replace: true });
    } catch (err) {
      handleRequestError(err);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="auth-page">
      <div className="auth-card">
        <img src="/logo.png" alt="REEVA" className="auth-logo" />
        <h1>Set a new password</h1>
        <form className="stack gap-md" onSubmit={onSubmit}>
          {!routeToken ? (
            <label className="field">
              <span>Reset token</span>
              <input
                value={manualToken}
                onChange={(e) => setManualToken(e.target.value)}
                required
              />
            </label>
          ) : null}
          <label className="field">
            <span>New password</span>
            <PasswordInput
              value={password}
              onChange={setPassword}
              autoComplete="new-password"
              show={showPassword}
              onToggleShow={() => setShowPassword((v) => !v)}
              required
            />
          </label>
          <label className="field">
            <span>Confirm password</span>
            <PasswordInput
              value={passwordConfirm}
              onChange={setPasswordConfirm}
              autoComplete="new-password"
              show={showPasswordConfirm}
              onToggleShow={() => setShowPasswordConfirm((v) => !v)}
              required
            />
          </label>
          {error ? <div className="banner error">{error}</div> : null}
          <button type="submit" className="btn btn-primary" disabled={submitting}>
            {submitting ? "Updating…" : "Update password"}
          </button>
        </form>
        <Link to="/login" className="center-link">
          Back to login
        </Link>
      </div>
    </div>
  );
};
